import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-services',
  standalone: true,
  imports: [RouterLink],
  template: `
    <main class="max-w-7xl mx-auto px-8 py-32">
      <!-- Hero Section -->
      <header class="mb-16">
        <div class="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span class="font-sans font-bold text-sm text-gold-accent mb-4 block tracking-widest uppercase">EXCELLENCE DEFINED</span>
            <h1 class="font-serif text-5xl text-on-surface mb-6">The pinnacle of home cleaning services.</h1>
            <p class="font-sans text-xl text-on-surface-variant max-w-xl">
              More than a spotless home, we deliver peace of mind. Our specialists are rigorously trained to provide sophisticated care that preserves and respects your home's unique character.
            </p>
          </div>
          <div class="relative h-[400px] overflow-hidden rounded-xl border-2 border-gold-accent shadow-lg">
            <img class="w-full h-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuD9t11g6M2EfAT32xcIxYGTE8KxhnqkXgZFfAH7U4j05ulApm5znLjUNOqOjszjsbcFdSkl40QoEiI9VvzX2rzne1rpr5GIAqnToysX3z67tubghmz2cgCzNY_NyFB2o_nlGJJz1RwBRkUSsPmA3YGFGlqlRwOXHRwGkMI2YCwHEGxaCmX5-UFhTP8EuNrnFhaVhvrbitHjWL9Xc4L3QH0tr_nYbkSNHAVAwwHAJM1jCTnDFW1rTbxAzB8gbk9VKMSdEKA4Mq0yAX4" alt="Luxury home interior">
          </div>
        </div>
      </header>

      <!-- Cleaning Standards Section -->
      <section class="mb-20">
        <div class="mb-12">
          <h2 class="font-serif text-4xl mb-4">Meticulous Cleaning Protocols</h2>
          <div class="w-24 h-1 bg-gold-accent mb-8"></div>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <!-- Deep Clean Card -->
          <div class="bg-white p-12 border border-stone-200 rounded-lg flex flex-col gap-6 shadow-sm hover:shadow-md transition-shadow">
            <div class="flex items-center gap-4">
              <span class="material-symbols-outlined text-4xl text-gold-accent">sanitizer</span>
              <h3 class="font-serif text-2xl">Hospital-Grade Sanitization</h3>
            </div>
            <p class="font-sans text-lg text-on-surface-variant">
              Utilizing hypoallergenic, non-toxic solutions that eliminate 99.9% of pathogens while preserving the air quality of your residence.
            </p>
            <ul class="space-y-4 pt-4 border-t border-stone-100">
              <li class="flex items-center gap-3 font-bold text-sm text-on-surface">
                <span class="material-symbols-outlined text-amber-600 text-xl" style="font-variation-settings: 'FILL' 1;">check_circle</span> HEPA Air Filtration
              </li>
              <li class="flex items-center gap-3 font-bold text-sm text-on-surface">
                <span class="material-symbols-outlined text-amber-600 text-xl" style="font-variation-settings: 'FILL' 1;">check_circle</span> UV-C Light Sterilization
              </li>
            </ul>
          </div>
          <!-- Professional Care Card -->
          <div class="bg-white p-12 border border-stone-200 rounded-lg flex flex-col gap-6 shadow-sm hover:shadow-md transition-shadow">
            <div class="flex items-center gap-4">
              <span class="material-symbols-outlined text-4xl text-gold-accent">inventory_2</span>
              <h3 class="font-serif text-2xl">Specialized Curatorship</h3>
            </div>
            <p class="font-sans text-lg text-on-surface-variant">
              Our staff is trained in the preservation of fine materials: marble, silk, heirloom wood, and delicate antiques.
            </p>
            <ul class="space-y-4 pt-4 border-t border-stone-100">
              <li class="flex items-center gap-3 font-bold text-sm text-on-surface">
                <span class="material-symbols-outlined text-amber-600 text-xl" style="font-variation-settings: 'FILL' 1;">check_circle</span> Material-Specific Buffing
              </li>
              <li class="flex items-center gap-3 font-bold text-sm text-on-surface">
                <span class="material-symbols-outlined text-amber-600 text-xl" style="font-variation-settings: 'FILL' 1;">check_circle</span> Antique Handling Protocol
              </li>
            </ul>
          </div>
          <!-- Consistency Card -->
          <div class="bg-white p-12 border border-stone-200 rounded-lg flex flex-col gap-6 shadow-sm hover:shadow-md transition-shadow">
            <div class="flex items-center gap-4">
              <span class="material-symbols-outlined text-4xl text-gold-accent">assignment_turned_in</span>
              <h3 class="font-serif text-2xl">50-Point Inspection</h3>
            </div>
            <p class="font-sans text-lg text-on-surface-variant">
              Every session concludes with a rigorous supervisor audit to ensure our signature 'Regency House Glow' is achieved.
            </p>
            <ul class="space-y-4 pt-4 border-t border-stone-100">
              <li class="flex items-center gap-3 font-bold text-sm text-on-surface">
                <span class="material-symbols-outlined text-amber-600 text-xl" style="font-variation-settings: 'FILL' 1;">check_circle</span> Digital Quality Logs
              </li>
              <li class="flex items-center gap-3 font-bold text-sm text-on-surface">
                <span class="material-symbols-outlined text-amber-600 text-xl" style="font-variation-settings: 'FILL' 1;">check_circle</span> Tailored Preferences Sync
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Specialized Training Section -->
      <section class="bg-stone-100 py-20 px-8 rounded-xl">
        <div class="grid md:grid-cols-2 gap-16 items-center">
          <div class="order-2 md:order-1">
            <div class="grid grid-cols-2 gap-6">
              <div class="bg-white p-8 rounded-lg border border-stone-100 shadow-sm text-center">
                <span class="material-symbols-outlined text-5xl mb-4 text-red-600">medical_services</span>
                <h4 class="font-bold text-sm mb-2">CPR Certified</h4>
                <p class="text-xs text-on-surface-variant">Emergency ready at all times.</p>
              </div>
              <div class="bg-white p-8 rounded-lg border border-stone-100 shadow-sm text-center">
                <span class="material-symbols-outlined text-5xl mb-4 text-primary">wounds_injuries</span>
                <h4 class="font-bold text-sm mb-2">First Aid Training</h4>
                <p class="text-xs text-on-surface-variant">Advanced safety protocols.</p>
              </div>
              <div class="bg-white p-8 rounded-lg border border-stone-100 shadow-sm text-center">
                <span class="material-symbols-outlined text-5xl mb-4 text-gold-accent">gavel</span>
                <h4 class="font-bold text-sm mb-2">Etiquette Honor</h4>
                <p class="text-xs text-on-surface-variant">Discreet and professional.</p>
              </div>
              <div class="bg-white p-8 rounded-lg border border-stone-100 shadow-sm text-center">
                <span class="material-symbols-outlined text-5xl mb-4 text-stone-600">verified_user</span>
                <h4 class="font-bold text-sm mb-2">Bonded & Insured</h4>
                <p class="text-xs text-on-surface-variant">Full peace of mind coverage.</p>
              </div>
            </div>
          </div>
          <div class="order-1 md:order-2">
            <span class="font-sans font-bold text-sm text-gold-accent mb-4 block tracking-widest uppercase">PEACE OF MIND</span>
            <h2 class="font-serif text-4xl mb-6">Staff Who Protect Your Sanctuary</h2>
            <p class="font-sans text-lg text-on-surface-variant mb-8">
              Every Regency House Steward undergoes hours of specialized training. This includes medical emergency response, concierge etiquette, and advanced material care. We don't just hire cleaners; we certify professionals.
            </p>
            <div class="space-y-6">
              <div class="flex gap-4 items-start">
                <div class="mt-1 bg-white border border-stone-200 p-2 rounded">
                  <span class="material-symbols-outlined text-gold-accent">security</span>
                </div>
                <div>
                  <h5 class="font-bold text-on-surface">Background Verified</h5>
                  <p class="text-base text-on-surface-variant">Continuous background monitoring and strict security vetting for every team member.</p>
                </div>
              </div>
              <div class="flex gap-4 items-start">
                <div class="mt-1 bg-white border border-stone-200 p-2 rounded">
                  <span class="material-symbols-outlined text-gold-accent">psychology</span>
                </div>
                <div>
                  <h5 class="font-bold text-on-surface">Empathetic Training</h5>
                  <p class="text-base text-on-surface-variant">Specially trained to work in homes with seniors, understanding unique needs and pace.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Operating Area Section -->
      <section class="mt-20 py-16 px-8 sm:px-12 bg-stone-50 border border-stone-200 rounded-xl">
        <div class="max-w-4xl mx-auto text-center space-y-6">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 border border-amber-600/30 rounded-full bg-amber-50">
            <span class="material-symbols-outlined text-amber-700 text-sm" style="font-variation-settings: 'FILL' 1;">location_on</span>
            <span class="font-sans font-bold text-xs text-amber-800 tracking-widest uppercase">Operating Exclusively in New York City</span>
          </div>

          <h2 class="font-serif text-3xl sm:text-4xl text-on-surface">
            Serving Premier Residences Across Manhattan, Brooklyn &amp; Queens
          </h2>

          <p class="font-sans text-lg text-on-surface-variant leading-relaxed">
            To ensure rapid response times, local steward familiarization, and white-glove building compliance, our services are concentrated solely within New York City. We are fully equipped for strict co-op and condominium requirements with Certificates of Insurance (COI) ready upon request.
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 text-left">
            <div class="bg-white p-6 rounded-lg border border-stone-200 shadow-sm space-y-2">
              <div class="flex items-center gap-2 text-stone-900 font-bold text-base">
                <span class="material-symbols-outlined text-gold-accent">domain</span>
                <h4 class="font-serif text-lg">Manhattan</h4>
              </div>
              <p class="text-xs text-on-surface-variant leading-relaxed">
                Upper East Side, Upper West Side, Tribeca, SoHo, West Village, Chelsea, Midtown &amp; Financial District.
              </p>
            </div>

            <div class="bg-white p-6 rounded-lg border border-stone-200 shadow-sm space-y-2">
              <div class="flex items-center gap-2 text-stone-900 font-bold text-base">
                <span class="material-symbols-outlined text-gold-accent">home_pin</span>
                <h4 class="font-serif text-lg">Brooklyn</h4>
              </div>
              <p class="text-xs text-on-surface-variant leading-relaxed">
                Brooklyn Heights, DUMBO, Park Slope, Cobble Hill, Carroll Gardens, Williamsburg &amp; Greenpoint.
              </p>
            </div>

            <div class="bg-white p-6 rounded-lg border border-stone-200 shadow-sm space-y-2">
              <div class="flex items-center gap-2 text-stone-900 font-bold text-base">
                <span class="material-symbols-outlined text-gold-accent">holiday_village</span>
                <h4 class="font-serif text-lg">Queens</h4>
              </div>
              <p class="text-xs text-on-surface-variant leading-relaxed">
                Long Island City, Astoria, Forest Hills &amp; Gardens, Sunnyside, Bayside &amp; Kew Gardens.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="mt-20 py-16 px-12 bg-stone-900 text-stone-50 rounded-xl text-center">
        <h2 class="font-serif text-4xl mb-6">Experience Uncompromising Quality</h2>
        <p class="font-sans text-xl text-stone-300 mb-10 max-w-2xl mx-auto">
          Join the many families who trust Regency House Cleaning to maintain the dignity and beauty of their most precious spaces.
        </p>
        <button class="bg-gold-accent text-black font-bold px-12 py-6 rounded-lg hover:bg-amber-400 transition-colors shadow-lg flex items-center justify-center gap-3 mx-auto min-h-[56px]" routerLink="/book">
          BOOK YOUR INITIAL VISIT
          <span class="material-symbols-outlined">arrow_forward</span>
        </button>
      </section>
    </main>
  `,
  styles: [`
    :host {
      display: block;
    }
  `],
})
export class ServicesComponent {}
